import React from 'react';
import BasicButton from '@/components/styled/BasicButton';
import { track } from '@/analytics';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight } from 'react-feather';
import { getUnseenUpdates, markUpdateAsSeen, APP_UPDATES, getAllSeenUpdates } from '@/config/appUpdates';
import { openWindow } from '@/utils';

// This is called from the SignupModal to have new signups not see the notification
export function setCurrentInAppNotificationSeen() {
    // Mark all current updates as seen for new signups
    APP_UPDATES.forEach((update) => {
        if (update.enabled) {
            markUpdateAsSeen(update.key);
        }
    });
}

// This component renders the notification itself, change the content as desired
export function InAppNotification() {
    const { t } = useTranslation();
    const [seenUpdates, setSeenUpdates] = React.useState<Set<string>>(() => getAllSeenUpdates());
    const [currentIndex, setCurrentIndex] = React.useState(0);

    const unseenUpdates = React.useMemo(() => getUnseenUpdates(seenUpdates), [seenUpdates]);
    const currentUpdate = unseenUpdates[currentIndex] || null;
    const hasMultiple = unseenUpdates.length > 1;

    function openChangelog() {
        if (!currentUpdate?.actionUrl) return;

        track(`${currentUpdate.key}-changelog`);

        openWindow(currentUpdate.actionUrl, '_blank');
    }

    function handleContinue() {
        if (!currentUpdate) return;

        track(`${currentUpdate.key}-continue`);
        markUpdateAsSeen(currentUpdate.key);

        // Update local state to remove current update
        const newSeenUpdates = new Set(seenUpdates);
        newSeenUpdates.add(currentUpdate.key);
        setSeenUpdates(newSeenUpdates);

        // If there are more updates, go to next one, otherwise close
        if (unseenUpdates.length > 1) {
            // Reset to first update (the list will be shorter now)
            setCurrentIndex(0);
        }
    }

    function dismissAll() {
        // Track dismissal of all updates
        unseenUpdates.forEach((update) => {
            track(`${update.key}-dismiss`);
            markUpdateAsSeen(update.key);
        });

        // Update state to mark all as seen
        setSeenUpdates((prev) => {
            const newSet = new Set(prev);
            unseenUpdates.forEach((update) => newSet.add(update.key));
            return newSet;
        });
    }

    function navigatePrevious() {
        setCurrentIndex((prev) => {
            const length = unseenUpdates.length;
            return length > 0 ? (prev - 1 + length) % length : 0;
        });
    }

    function navigateNext() {
        setCurrentIndex((prev) => {
            const length = unseenUpdates.length;
            return length > 0 ? (prev + 1) % length : 0;
        });
    }

    if (!currentUpdate || unseenUpdates.length === 0) {
        return null;
    }

    return (
        <div className="fixed bottom-14 left-1.5 z-50 animate-fadeIn" role="alert" aria-live="polite">
            <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-gray-200 dark:border-zinc-700 max-w-xs overflow-hidden">
                <div className="relative">
                    <button
                        onClick={dismissAll}
                        className="absolute top-2 right-2 z-10 p-1 rounded hover:bg-saga-gray-dark/10 dark:hover:bg-zinc-700/50 focus:outline-none focus:ring-2 focus:ring-saga-blue focus:ring-offset-2"
                        aria-label="Close"
                    >
                        <X size={16} className="text-saga-gray-dark dark:text-zinc-200" />
                    </button>

                    {currentUpdate.image && (
                        <img
                            src={currentUpdate.image}
                            alt={currentUpdate.title}
                            className="w-full rounded-t-lg"
                            loading="lazy"
                        />
                    )}

                    <div className="p-4 space-y-3">
                        <h3 className="font-semibold text-saga-black dark:text-zinc-200">{currentUpdate.title}</h3>

                        <p className="text-sm text-saga-gray-dark dark:text-zinc-400">{currentUpdate.description}</p>

                        {hasMultiple && (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-1">
                                    <button
                                        onClick={navigatePrevious}
                                        className="p-1 rounded hover:bg-saga-gray-100 dark:hover:bg-zinc-700 focus:outline-none"
                                        aria-label="Previous update"
                                    >
                                        <ChevronLeft size={16} className="text-saga-gray-dark dark:text-zinc-400" />
                                    </button>
                                    <button
                                        onClick={navigateNext}
                                        className="p-1 rounded hover:bg-saga-gray-100 dark:hover:bg-zinc-700 focus:outline-none"
                                        aria-label="Next update"
                                    >
                                        <ChevronRight size={16} className="text-saga-gray-dark dark:text-zinc-400" />
                                    </button>
                                </div>

                                <div className="flex items-center space-x-2">
                                    {currentUpdate.actionUrl && (
                                        <BasicButton
                                            onClick={openChangelog}
                                            variant="tertiary"
                                            size="small"
                                            className="px-3 py-2.5"
                                        >
                                            {t('common.changelog')}
                                        </BasicButton>
                                    )}
                                    <BasicButton
                                        onClick={handleContinue}
                                        variant="secondary"
                                        size="small"
                                        className="px-3 py-2.5"
                                    >
                                        {t('common.continue')}
                                    </BasicButton>
                                </div>
                            </div>
                        )}

                        {!hasMultiple && (
                            <div className="flex justify-end space-x-2">
                                {currentUpdate.actionUrl && (
                                    <BasicButton
                                        onClick={openChangelog}
                                        variant="tertiary"
                                        size="small"
                                        className="px-3 py-2.5"
                                    >
                                        {t('common.changelog')}
                                    </BasicButton>
                                )}
                                <BasicButton
                                    onClick={handleContinue}
                                    variant="secondary"
                                    size="small"
                                    className="px-3 py-2.5"
                                >
                                    {t('common.continue')}
                                </BasicButton>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
